import { useEffect, useRef, useState, type MouseEvent } from 'react';
import axios from 'axios';
import { useAtomValue } from 'jotai';
import { MdChatBubbleOutline } from 'react-icons/md';
import { FaHeart, FaRegHeart } from 'react-icons/fa';
import MainURL from '../../MainURL';
import { loginStateAtom, userDataAtom } from '../../AtomStore';
import type { CommunityBoardConfig, CommunityPost } from './BoardTypes';
import { getIsLikedRoute, getIsLikedToggleRoute } from './boardListUtils';

type Props = {
  config: CommunityBoardConfig;
  post: CommunityPost;
  contentText: string;
  dateLabel: string;
  onOpenDetail: () => void;
};

export default function BoardFeedListItem({ config, post, contentText, dateLabel, onOpenDetail }: Props) {
  const bodyRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  const isLogin = useAtomValue(loginStateAtom);
  const userData = useAtomValue(userDataAtom);
  const [likeCount, setLikeCount] = useState(Number(post.isLiked) || 0);
  const [checkIsLiked, setCheckIsLiked] = useState(false);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el || expanded) return;
    setIsTruncated(el.scrollHeight > el.clientHeight + 1);
  }, [contentText, expanded]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${MainURL}/${config.apiBase}/${getIsLikedRoute(config)}/${post.id}`)
      .then((res) => {
        if (cancelled) return;
        if (res.data) {
          const rows = res.data as { userAccount: string; isliked: string }[];
          setLikeCount(rows.length);
          const mine = rows.filter((row) => row.userAccount === userData.userAccount);
          setCheckIsLiked(mine.length > 0 && mine[0].isliked === 'true');
        } else {
          setLikeCount(0);
          setCheckIsLiked(false);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [config, post.id, userData.userAccount]);

  const toggleLike = (e: MouseEvent) => {
    e.stopPropagation();
    if (!isLogin || !userData.userAccount?.trim()) {
      alert('권한이 없습니다. 로그인이 필요합니다.');
      return;
    }
    axios
      .post(`${MainURL}/${config.apiBase}/${getIsLikedToggleRoute(config)}`, {
        postId: post.id,
        isLiked: checkIsLiked,
        userAccount: userData.userAccount,
      })
      .then((res) => {
        if (res.data) {
          setCheckIsLiked(!checkIsLiked);
          setLikeCount((count) => (checkIsLiked ? Math.max(0, count - 1) : count + 1));
        }
      })
      .catch(() => {
        alert('좋아요 처리에 실패했습니다.');
      });
  };

  const handleMore = (e: MouseEvent) => {
    e.stopPropagation();
    setExpanded(true);
  };

  return (
    <div
      className="board-feed-item"
      onClick={onOpenDetail}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpenDetail();
      }}
      role="button"
      tabIndex={0}
    >
      <div className="board-feed-item__header">
        <span className="board-feed-item__badge">{post.sort}</span>
        <p className="board-feed-item__title">
          {post.title}
          {post.commentCount ? (
            <span className="community-board__comment"> [{post.commentCount}]</span>
          ) : null}
        </p>
      </div>

      <p
        ref={bodyRef}
        className={
          expanded ? 'board-feed-item__body' : 'board-feed-item__body board-feed-item__body--clamped'
        }
      >
        {contentText}
      </p>
      {isTruncated && !expanded ? (
        <button type="button" className="board-feed-item__more" onClick={handleMore}>
          ...더보기
        </button>
      ) : null}

      <div className="board-feed-item__footer">
        <span className="board-feed-item__meta">
          {post.userNickName} · {dateLabel}
        </span>
        <div className="board-feed-item__actions">
          <button type="button" className="board-feed-item__action" onClick={toggleLike}>
            {checkIsLiked ? <FaHeart color="#325382" /> : <FaRegHeart color="#8e8e8e" />}
            <span className={checkIsLiked ? 'is-active' : ''}>{likeCount}</span>
          </button>
          <button type="button" className="board-feed-item__action" onClick={onOpenDetail}>
            <MdChatBubbleOutline color="#8e8e8e" />
            <span>{post.commentCount ?? 0}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
